import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getAdminCompanies, getAdminCompanyDetails, getAdminSectionData, deleteAdminItem, updateAdminSubscription, getAdminPlans, createAdminCompany, deleteAdminCompany, addAdminCredits, getAdminSiteAnalytics } from '../lib/adminBackendApi';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import './AdminDashboard.css';
import AppIcon from './icons/AppIcon';

const SEGMENTOS = [
  { value: '', label: 'Selecione um segmento' },
  { value: 'alimentacao', label: 'Alimentação' },
  { value: 'imoveis', label: 'Imóveis' },
  { value: 'tecnologia', label: 'Tecnologia' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'outros', label: 'Outros' },
];

const SUBSEGMENTOS = {
  alimentacao: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'pizzaria', label: 'Pizzaria' },
    { value: 'lanchonete', label: 'Lanchonete' },
    { value: 'hamburgueria', label: 'Hamburgueria' },
    { value: 'comida_oriental', label: 'Comida Oriental' },
    { value: 'restaurante', label: 'Restaurante' },
  ],
  imoveis: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'imobiliaria', label: 'Imobiliária' },
    { value: 'corretor', label: 'Corretor' },
  ],
  tecnologia: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'servicos', label: 'Serviços' },
    { value: 'produtos_geral', label: 'Produtos em Geral' },
  ],
  ecommerce: [
    { value: '', label: 'Selecione o tipo' },
    { value: 'moda', label: 'Moda' },
  ],
  outros: [],
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, empresas
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    planStats: []
  });
  
  // Estados para aba Empresas
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [sectionData, setSectionData] = useState({
    users: [],
    agents: [],
    connections: [],
    contacts: [],
    conversations: []
  });
  const [loadingSection, setLoadingSection] = useState(false);
  
  // Estados para edição de plano
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [editPlanData, setEditPlanData] = useState({
    plan_id: '',
    status: 'active',
    renewal_date: ''
  });
  const [availablePlans, setAvailablePlans] = useState([]);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [createCompanyError, setCreateCompanyError] = useState('');
  const [createCompanyForm, setCreateCompanyForm] = useState({
    nome_empresa: '',
    segmento: '',
    subsegmento: '',
    email: '',
    whatsapp: '',
    senha: '',
    confirmar_senha: '',
    plan_id: ''
  });
  const [creditAmount, setCreditAmount] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);
  
  // Estados para gráficos e métricas
  const [chartPeriod, setChartPeriod] = useState('7days'); // 7days, 30days
  const [newCompaniesMetric, setNewCompaniesMetric] = useState('today'); // today, 7days, 30days, thisMonth
  const [chartData, setChartData] = useState([]);
  const [newCompaniesCount, setNewCompaniesCount] = useState(0);

  // Estados para analytics do Site Halo
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7days');
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [gasAguaAnalytics, setGasAguaAnalytics] = useState(null);
  const [loadingGasAguaAnalytics, setLoadingGasAguaAnalytics] = useState(false);
  const [gasAguaAnalyticsError, setGasAguaAnalyticsError] = useState('');

  useEffect(() => {
    loadDashboardData();
    loadAvailablePlans();
    loadChartData();
    loadNewCompaniesMetric();
  }, []);

  useEffect(() => {
    loadChartData();
  }, [chartPeriod]);

  useEffect(() => {
    loadNewCompaniesMetric();
  }, [newCompaniesMetric]);

  useEffect(() => {
    if (activeTab === 'site-halo') {
      loadSiteAnalytics();
      loadGasAguaAnalytics();
    }
  }, [activeTab, analyticsPeriod]);

  const loadSiteAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      setAnalyticsError('');
      const payload = await getAdminSiteAnalytics(analyticsPeriod);
      setSiteAnalytics(payload);
    } catch (error) {
      console.error('Erro ao carregar analytics do site:', error);
      setAnalyticsError(error?.message || 'Erro ao carregar analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadGasAguaAnalytics = async () => {
    try {
      setLoadingGasAguaAnalytics(true);
      setGasAguaAnalyticsError('');
      const payload = await getAdminSiteAnalytics(analyticsPeriod, '/gas-agua');
      setGasAguaAnalytics(payload);
    } catch (error) {
      console.error('Erro ao carregar analytics Gas e Água:', error);
      setGasAguaAnalyticsError(error?.message || 'Erro ao carregar analytics Gas e Água');
    } finally {
      setLoadingGasAguaAnalytics(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // 1. Total de empresas
      const { count: companiesCount, error: companiesError } = await supabase
        .from('configuracoes_empresa')
        .select('*', { count: 'exact', head: true });

      if (companiesError) throw companiesError;

      // 2. Total de usuários
      const { count: usersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      // 3. Estatísticas por plano
      const { data: plansData, error: plansError } = await supabase
        .from('plans')
        .select('id, name, slug, monthly_credits, price_brl')
        .eq('is_active', true)
        .order('display_order');

      if (plansError) throw plansError;

      // 4. Contar usuários por plano
      const planStatsPromises = plansData.map(async (plan) => {
        const { count, error } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('plano_conta', plan.slug);

        if (error) {
          console.error(`Erro ao contar usuários do plano ${plan.slug}:`, error);
          return { ...plan, userCount: 0 };
        }

        return { ...plan, userCount: count || 0 };
      });

      const planStats = await Promise.all(planStatsPromises);

      setStats({
        totalCompanies: companiesCount || 0,
        totalUsers: usersCount || 0,
        planStats
      });

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    try {
      if (!selectedCompany) {
        alert('Selecione uma empresa antes de adicionar créditos.');
        return;
      }

      const creditsValue = Number(creditAmount);
      if (!Number.isFinite(creditsValue) || creditsValue <= 0) {
        alert('Informe uma quantidade válida de créditos.');
        return;
      }

      setAddingCredits(true);
      const updatedCredits = await addAdminCredits(selectedCompany, creditsValue);

      setCompanyDetails((prev) => prev ? { ...prev, credits: updatedCredits } : prev);
      setCreditAmount('');
      alert('Créditos adicionados com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar créditos:', error);
      alert('Erro ao adicionar créditos: ' + error.message);
    } finally {
      setAddingCredits(false);
    }
  };

  const handleOpenCreateCompany = () => {
    setCreateCompanyError('');
    setShowCreateCompanyModal(true);
  };

  const handleCloseCreateCompany = () => {
    if (creatingCompany) return;
    setShowCreateCompanyModal(false);
    setCreateCompanyForm({
      nome_empresa: '',
      segmento: '',
      subsegmento: '',
      email: '',
      whatsapp: '',
      senha: '',
      confirmar_senha: '',
      plan_id: ''
    });
    setCreateCompanyError('');
  };

  const handleCreateCompanyChange = (e) => {
    const { name, value } = e.target;
    setCreateCompanyForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'segmento' ? { subsegmento: '' } : {})
    }));
  };

  const handleCreateCompanySubmit = async (e) => {
    e.preventDefault();
    setCreateCompanyError('');

    const {
      nome_empresa,
      segmento,
      subsegmento,
      email,
      whatsapp,
      senha,
      confirmar_senha,
      plan_id
    } = createCompanyForm;

    if (!nome_empresa || !email || !whatsapp || !senha || !confirmar_senha || !plan_id) {
      setCreateCompanyError('Preencha todos os campos obrigatórios.');
      return;
    }

    if (senha.length < 6) {
      setCreateCompanyError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (senha !== confirmar_senha) {
      setCreateCompanyError('As senhas não coincidem.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setCreateCompanyError('E-mail inválido.');
      return;
    }

    setCreatingCompany(true);
    try {
      await createAdminCompany({
        nome_empresa,
        segmento: segmento || null,
        subsegmento: subsegmento || null,
        email,
        whatsapp,
        senha,
        plan_id
      });

      alert('Empresa criada com sucesso!');
      handleCloseCreateCompany();
      await loadCompanies();
      await loadDashboardData();
    } catch (error) {
      setCreateCompanyError(error?.message || 'Erro ao criar empresa.');
    } finally {
      setCreatingCompany(false);
    }
  };

  const loadCompanies = async () => {
    try {
      setLoading(true);
      
      // Chamar backend Express com Service Role
      const data = await getAdminCompanies();
      setCompanies(data || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      alert('Erro ao carregar empresas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados do gráfico de empresas novas
  const loadChartData = async () => {
    try {
      const days = chartPeriod === '7days' ? 7 : 30;
      const data = [];
      
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        
        const { count } = await supabase
          .from('configuracoes_empresa')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', date.toISOString())
          .lt('created_at', nextDate.toISOString());
        
        data.push({
          date: date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
          empresas: count || 0
        });
      }
      
      setChartData(data);
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    }
  };

  // Carregar métrica de novas empresas
  const loadNewCompaniesMetric = async () => {
    try {
      let startDate = new Date();
      
      switch (newCompaniesMetric) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case '7days':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30days':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case 'thisMonth':
          startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
          break;
      }
      
      const { count } = await supabase
        .from('configuracoes_empresa')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString());
      
      setNewCompaniesCount(count || 0);
    } catch (error) {
      console.error('Erro ao carregar métrica de novas empresas:', error);
    }
  };

  const handleOpenCompanyModal = async (companyId) => {
    setSelectedCompany(companyId);
    setShowCompanyModal(true);
    await loadCompanyDetails(companyId);
  };

  const handleCloseCompanyModal = () => {
    setShowCompanyModal(false);
    setSelectedCompany(null);
    setCompanyDetails(null);
    setCreditAmount('');
    setExpandedSection(null);
    setSectionData({
      users: [],
      agents: [],
      connections: [],
      contacts: [],
      conversations: []
    });
  };

  const loadSectionData = async (section, companyId) => {
    try {
      setLoadingSection(true);

      console.log(`Carregando ${section} para empresa:`, companyId);

      // Chamar backend Express com Service Role
      const data = await getAdminSectionData(companyId, section);
      
      console.log(`${section} encontrados:`, data?.length || 0);

      setSectionData(prev => ({
        ...prev,
        [section]: data || []
      }));

    } catch (error) {
      console.error(`Erro ao carregar ${section}:`, error);
      alert(`Erro ao carregar ${section}: ` + error.message);
    } finally {
      setLoadingSection(false);
    }
  };

  const handleToggleSection = async (section) => {
    console.log('Toggle section:', section, 'Current expanded:', expandedSection);
    console.log('Selected company:', selectedCompany);
    console.log('Current section data:', sectionData[section]);
    
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
      if (selectedCompany && sectionData[section].length === 0) {
        console.log('Carregando dados da seção:', section);
        await loadSectionData(section, selectedCompany);
      } else {
        console.log('Dados já carregados ou empresa não selecionada');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;

    try {
      // Chamar backend Express com Service Role
      await deleteAdminItem('user', userId);

      // Recarregar lista
      await loadSectionData('users', selectedCompany);
      alert('Usuário excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('Erro ao excluir usuário: ' + error.message);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este agente?')) return;

    try {
      // Chamar backend Express com Service Role
      await deleteAdminItem('agent', agentId);

      // Recarregar lista
      await loadSectionData('agents', selectedCompany);
      alert('Agente excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir agente:', error);
      alert('Erro ao excluir agente: ' + error.message);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conexão?')) return;

    try {
      // Chamar backend Express com Service Role
      await deleteAdminItem('connection', connectionId);

      // Recarregar lista
      await loadSectionData('connections', selectedCompany);
      alert('Conexão excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir conexão:', error);
      alert('Erro ao excluir conexão: ' + error.message);
    }
  };

  // Carregar planos disponíveis
  const loadAvailablePlans = async () => {
    try {
      const plans = await getAdminPlans();
      setAvailablePlans(plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  // Abrir modal de edição de plano
  const handleOpenEditPlan = () => {
    setEditPlanData({
      plan_id: companyDetails.subscription?.plan_id || '',
      status: companyDetails.subscription?.status || 'active',
      renewal_date: companyDetails.subscription?.expires_at 
        ? new Date(companyDetails.subscription.expires_at).toISOString().split('T')[0]
        : ''
    });
    setShowEditPlanModal(true);
  };

  // Salvar alterações do plano
  const handleSavePlan = async () => {
    try {
      const { plan_id, status, renewal_date } = editPlanData;

      // Atualizar plano
      await updateAdminSubscription(selectedCompany, plan_id, status, renewal_date);

      // Perguntar se quer atualizar os créditos
      if (plan_id) {
        const selectedPlan = availablePlans.find(p => p.id === plan_id);
        if (selectedPlan && selectedPlan.monthly_credits) {
          const updateCredits = window.confirm(
            `Deseja atualizar os créditos para ${selectedPlan.monthly_credits.toLocaleString('pt-BR')} créditos (valor do plano ${selectedPlan.name})?`
          );

          if (updateCredits) {
            // Atualizar créditos no banco
            const { data: existingCredits } = await supabase
              .from('company_credits')
              .select('id')
              .eq('company_id', selectedCompany)
              .maybeSingle();

            if (existingCredits) {
              // Atualizar créditos existentes
              await supabase
                .from('company_credits')
                .update({
                  total_credits: selectedPlan.monthly_credits,
                  updated_at: new Date().toISOString()
                })
                .eq('id', existingCredits.id);
            } else {
              // Criar registro de créditos
              await supabase
                .from('company_credits')
                .insert({
                  company_id: selectedCompany,
                  total_credits: selectedPlan.monthly_credits,
                  used_credits: 0
                });
            }
          }
        }
      }

      alert('Plano atualizado com sucesso!');
      setShowEditPlanModal(false);
      await loadCompanyDetails(selectedCompany);
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      alert('Erro ao atualizar plano: ' + error.message);
    }
  };

  // Deletar empresa
  const handleDeleteCompany = async () => {
    const confirmText = `DELETAR ${companyDetails.nome_fantasia}`;
    const userInput = prompt(
      `⚠️ ATENÇÃO! Esta ação é IRREVERSÍVEL!\n\n` +
      `Você está prestes a deletar a empresa "${companyDetails.nome_fantasia}" e TODOS os dados relacionados:\n` +
      `- Todos os usuários\n` +
      `- Todos os agentes\n` +
      `- Todas as conexões\n` +
      `- Todos os contatos\n` +
      `- Todas as conversas\n` +
      `- Projetos e itens\n` +
      `- Assinaturas e créditos\n\n` +
      `Para confirmar, digite: ${confirmText}`
    );

    if (userInput !== confirmText) {
      alert('Operação cancelada. O texto digitado não confere.');
      return;
    }

    try {
      // Usar endpoint do backend com service_role para deletar
      await deleteAdminCompany(selectedCompany);

      alert('Empresa deletada com sucesso!');
      setShowCompanyModal(false);
      setSelectedCompany(null);
      setCompanyDetails(null);
      await loadCompanies();
    } catch (error) {
      console.error('Erro ao deletar empresa:', error);
      alert('Erro ao deletar empresa: ' + error.message);
    }
  };

  const loadCompanyDetails = async (companyId) => {
    try {
      setLoadingDetails(true);

      // Chamar backend Express com Service Role
      const details = await getAdminCompanyDetails(companyId);
      
      console.log('Detalhes da empresa carregados:', {
        companyId,
        hasSubscription: !!details.subscription,
        hasCredits: !!details.credits,
        subscription: details.subscription,
        credits: details.credits
      });

      setCompanyDetails(details);

    } catch (error) {
      console.error('Erro ao carregar detalhes da empresa:', error);
      alert('Erro ao carregar detalhes: ' + error.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'empresas' && companies.length === 0) {
      loadCompanies();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Tabs */}
      <div className="settings-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <AppIcon name="dashboard" />
          Visão Geral
        </button>
        <button 
          className={`tab-button ${activeTab === 'empresas' ? 'active' : ''}`}
          onClick={() => setActiveTab('empresas')}
        >
          <AppIcon name="business" />
          Empresas
        </button>
        <button 
          className={`tab-button ${activeTab === 'site-halo' ? 'active' : ''}`}
          onClick={() => setActiveTab('site-halo')}
        >
          <AppIcon name="language" />
          Site Halo
        </button>
      </div>

      {/* Conteúdo */}
      <div className="settings-content">
        {activeTab === 'overview' && (
          <>
          {/* Cards de Estatísticas Gerais */}
          <div className="stats-grid">
        {/* Total de Empresas */}
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <AppIcon name="business" />
          </div>
          <div className="stat-content">
            <h3>Empresas Cadastradas</h3>
            <p className="stat-number">{stats.totalCompanies}</p>
          </div>
        </div>

        {/* Total de Usuários */}
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <AppIcon name="group" />
          </div>
          <div className="stat-content">
            <h3>Usuários Cadastrados</h3>
            <p className="stat-number">{stats.totalUsers}</p>
          </div>
        </div>

        {/* Total de Planos Ativos */}
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <AppIcon name="workspace_premium" />
          </div>
          <div className="stat-content">
            <h3>Planos Disponíveis</h3>
            <p className="stat-number">{stats.planStats.length}</p>
          </div>
        </div>

        {/* Média de Usuários por Plano */}
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <AppIcon name="analytics" />
          </div>
          <div className="stat-content">
            <h3>Média por Plano</h3>
            <p className="stat-number">
              {stats.planStats.length > 0 
                ? Math.round(stats.totalUsers / stats.planStats.length) 
                : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Card de Novas Empresas com Seletor */}
      <div className="metric-card-wrapper">
        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon">
              <AppIcon name="add_business" />
            </div>
            <div className="metric-info">
              <h3>Novas Empresas</h3>
              <p className="metric-value">{newCompaniesCount}</p>
            </div>
          </div>
          <div className="metric-selector">
            <button 
              className={`metric-btn ${newCompaniesMetric === 'today' ? 'active' : ''}`}
              onClick={() => setNewCompaniesMetric('today')}
            >
              Hoje
            </button>
            <button 
              className={`metric-btn ${newCompaniesMetric === '7days' ? 'active' : ''}`}
              onClick={() => setNewCompaniesMetric('7days')}
            >
              7 dias
            </button>
            <button 
              className={`metric-btn ${newCompaniesMetric === '30days' ? 'active' : ''}`}
              onClick={() => setNewCompaniesMetric('30days')}
            >
              30 dias
            </button>
            <button 
              className={`metric-btn ${newCompaniesMetric === 'thisMonth' ? 'active' : ''}`}
              onClick={() => setNewCompaniesMetric('thisMonth')}
            >
              Este Mês
            </button>
          </div>
        </div>
      </div>

      {/* Gráfico de Empresas Novas */}
      <div className="chart-section">
        <div className="chart-header">
          <h2>
            <AppIcon name="trending_up" />
            Crescimento de Empresas
          </h2>
          <div className="chart-period-selector">
            <button 
              className={`period-btn ${chartPeriod === '7days' ? 'active' : ''}`}
              onClick={() => setChartPeriod('7days')}
            >
              Últimos 7 dias
            </button>
            <button 
              className={`period-btn ${chartPeriod === '30days' ? 'active' : ''}`}
              onClick={() => setChartPeriod('30days')}
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <defs>
                <linearGradient id="colorEmpresas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#44e1ff" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#b43bea" stopOpacity={0.2}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(180, 59, 234, 0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="#9bb3c7"
                style={{ fontSize: '0.85rem' }}
              />
              <YAxis 
                stroke="#9bb3c7"
                style={{ fontSize: '0.85rem' }}
              />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(15, 12, 41, 0.95)',
                  border: '1px solid rgba(180, 59, 234, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="empresas" 
                stroke="#44e1ff"
                strokeWidth={3}
                fill="url(#colorEmpresas)"
                dot={{ fill: '#44e1ff', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabela de Planos */}
      <div className="plans-section">
        <div className="section-header">
          <h2>
            <AppIcon name="workspace_premium" />
            Distribuição por Plano
          </h2>
        </div>

        <div className="plans-table-container">
          <table className="plans-table">
            <thead>
              <tr>
                <th>Plano</th>
                <th>Slug</th>
                <th>Créditos Mensais</th>
                <th>Preço (R$)</th>
                <th>Usuários</th>
                <th>Percentual</th>
              </tr>
            </thead>
            <tbody>
              {stats.planStats.map((plan) => {
                const percentage = stats.totalUsers > 0 
                  ? ((plan.userCount / stats.totalUsers) * 100).toFixed(1)
                  : 0;

                return (
                  <tr key={plan.id}>
                    <td>
                      <div className="plan-name">
                        <AppIcon name="workspace_premium" />
                        <strong>{plan.name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="plan-slug">{plan.slug}</span>
                    </td>
                    <td>
                      <span className="plan-credits">
                        {plan.monthly_credits?.toLocaleString('pt-BR') || '—'}
                      </span>
                    </td>
                    <td>
                      <span className="plan-price">
                        {plan.price_brl 
                          ? `R$ ${plan.price_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                          : 'Gratuito'}
                      </span>
                    </td>
                    <td>
                      <span className="user-count">{plan.userCount}</span>
                    </td>
                    <td>
                      <div className="percentage-bar">
                        <div 
                          className="percentage-fill" 
                          style={{ width: `${percentage}%` }}
                        />
                        <span className="percentage-text">{percentage}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {stats.planStats.length === 0 && (
            <div className="empty-state">
              <AppIcon name="inbox" />
              <p>Nenhum plano cadastrado</p>
            </div>
          )}
        </div>
      </div>
          </>
        )}

        {activeTab === 'site-halo' && (
          <div className="details-section">
            <h4>
              <AppIcon name="monitoring" />
              Dashboard do Site Halo
            </h4>
            <div className="filters-row">
              <div className="period-selector">
                {['today', '7days', '30days'].map((period) => (
                  <button
                    key={period}
                    className={`tab-button ${analyticsPeriod === period ? 'active' : ''}`}
                    onClick={() => setAnalyticsPeriod(period)}
                  >
                    {period === 'today' ? 'Hoje' : period === '30days' ? '30 dias' : '7 dias'}
                  </button>
                ))}
              </div>
              <button
                className="edit-button"
                onClick={() => {
                  loadSiteAnalytics();
                  loadGasAguaAnalytics();
                }}
                disabled={loadingAnalytics || loadingGasAguaAnalytics}
              >
                <AppIcon name="refresh" />
                Atualizar
              </button>
            </div>

            {analyticsError && (
              <div className="empty-state">
                <AppIcon name="error" />
                <p>{analyticsError}</p>
              </div>
            )}

            {loadingAnalytics && (
              <div className="empty-state">
                <AppIcon name="hourglass_top" />
                <p>Carregando métricas...</p>
              </div>
            )}

            {gasAguaAnalyticsError && (
              <div className="empty-state">
                <AppIcon name="error" />
                <p>{gasAguaAnalyticsError}</p>
              </div>
            )}

            {loadingGasAguaAnalytics && (
              <div className="empty-state">
                <AppIcon name="hourglass_top" />
                <p>Carregando dashboard Gas e Água...</p>
              </div>
            )}

            {!loadingAnalytics && siteAnalytics?.ok && (
              <>
                <div className="stats-grid-small">
                  <div className="stat-card-small">
                    <span className="stat-label">Visitas</span>
                    <span className="stat-value">{siteAnalytics.summary?.pageviews || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">Cliques</span>
                    <span className="stat-value">{siteAnalytics.summary?.clicks || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">IPs únicos</span>
                    <span className="stat-value">{siteAnalytics.summary?.unique_ips || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">Tempo médio</span>
                    <span className="stat-value">{formatDuration(siteAnalytics.summary?.avg_duration_ms || 0)}</span>
                  </div>
                </div>

                <div className="details-grid">
                  <div className="detail-card">
                    <h5>Top páginas</h5>
                    {siteAnalytics.top_pages?.length ? (
                      <ul className="simple-list">
                        {siteAnalytics.top_pages.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="empty-inline">Sem dados</p>
                    )}
                  </div>
                  <div className="detail-card">
                    <h5>Top países</h5>
                    {siteAnalytics.top_countries?.length ? (
                      <ul className="simple-list">
                        {siteAnalytics.top_countries.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="empty-inline">Sem dados</p>
                    )}
                  </div>
                  <div className="detail-card">
                    <h5>Top cidades</h5>
                    {siteAnalytics.top_cities?.length ? (
                      <ul className="simple-list">
                        {siteAnalytics.top_cities.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="empty-inline">Sem dados</p>
                    )}
                  </div>
                  <div className="detail-card">
                    <h5>Top origens</h5>
                    {siteAnalytics.top_referrers?.length ? (
                      <ul className="simple-list">
                        {siteAnalytics.top_referrers.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="empty-inline">Sem dados</p>
                    )}
                  </div>
                </div>

                <div className="details-section">
                  <h5>Visitas e cliques por dia</h5>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={siteAnalytics.timeseries || []}>
                        <defs>
                          <linearGradient id="haloPageviews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#44e1ff" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#44e1ff" stopOpacity={0.1} />
                          </linearGradient>
                          <linearGradient id="haloClicks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#b43bea" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#b43bea" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="label" stroke="#9bb3c7" />
                        <YAxis stroke="#9bb3c7" />
                        <Tooltip
                          formatter={(value, name) => {
                            const label = name === 'pageviews' ? 'Visitas' : 'Cliques';
                            return [value, label];
                          }}
                          labelFormatter={(label) => `Dia ${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey="pageviews"
                          stroke="#44e1ff"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="clicks"
                          stroke="#b43bea"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {!loadingGasAguaAnalytics && gasAguaAnalytics?.ok && (
              <div className="details-section gasagua-dashboard">
                <h5>Dashboard Gas e Água</h5>
                <div className="stats-grid-small gasagua-kpis">
                  <div className="stat-card-small">
                    <span className="stat-label">Pageviews</span>
                    <span className="stat-value">{gasAguaAnalytics.summary?.pageviews || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">Cliques</span>
                    <span className="stat-value">{gasAguaAnalytics.summary?.clicks || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">IPs únicos</span>
                    <span className="stat-value">{gasAguaAnalytics.summary?.unique_ips || 0}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">CTR</span>
                    <span className="stat-value">{formatPercent((gasAguaAnalytics.summary?.ctr || 0) * 100)}</span>
                  </div>
                  <div className="stat-card-small">
                    <span className="stat-label">Tempo médio</span>
                    <span className="stat-value">{formatDuration(gasAguaAnalytics.summary?.avg_duration_ms || 0)}</span>
                  </div>
                </div>

                <div className="details-grid gasagua-deltas">
                  <div className="detail-card">
                    <h5>Variação vs período anterior</h5>
                    <ul className="simple-list">
                      <li>
                        <span>Pageviews</span>
                        <strong>{gasAguaAnalytics.summary?.delta?.pageviews ?? 0}</strong>
                      </li>
                      <li>
                        <span>Cliques</span>
                        <strong>{gasAguaAnalytics.summary?.delta?.clicks ?? 0}</strong>
                      </li>
                      <li>
                        <span>CTR</span>
                        <strong>{formatPercent((gasAguaAnalytics.summary?.delta?.ctr || 0) * 100)}</strong>
                      </li>
                    </ul>
                  </div>
                  <div className="detail-card">
                    <h5>UTM Source</h5>
                    {gasAguaAnalytics.top_utm_source?.length ? (
                      <ul className="simple-list">
                        {gasAguaAnalytics.top_utm_source.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="empty-inline">Sem dados</p>}
                  </div>
                  <div className="detail-card">
                    <h5>UTM Campaign</h5>
                    {gasAguaAnalytics.top_utm_campaign?.length ? (
                      <ul className="simple-list">
                        {gasAguaAnalytics.top_utm_campaign.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="empty-inline">Sem dados</p>}
                  </div>
                  <div className="detail-card">
                    <h5>UTM Medium</h5>
                    {gasAguaAnalytics.top_utm_medium?.length ? (
                      <ul className="simple-list">
                        {gasAguaAnalytics.top_utm_medium.map((item) => (
                          <li key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="empty-inline">Sem dados</p>}
                  </div>
                </div>

                <div className="details-grid">
                  <div className="detail-card">
                    <h5>Evolução diária (views x clicks)</h5>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={gasAguaAnalytics.timeseries || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis dataKey="label" stroke="#9bb3c7" />
                          <YAxis stroke="#9bb3c7" />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="pageviews" stroke="#44e1ff" strokeWidth={2} name="Pageviews" />
                          <Line type="monotone" dataKey="clicks" stroke="#b43bea" strokeWidth={2} name="Cliques" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="detail-card">
                    <h5>CTR diário (%)</h5>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={(gasAguaAnalytics.timeseries || []).map((item) => ({
                          ...item,
                          ctr_percent: Number(((item.ctr || 0) * 100).toFixed(2))
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis dataKey="label" stroke="#9bb3c7" />
                          <YAxis stroke="#9bb3c7" />
                          <Tooltip formatter={(value) => [`${value}%`, 'CTR']} />
                          <Bar dataKey="ctr_percent" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'empresas' && (
          <>
            <div className="companies-actions">
              <button className="primary-action-button" onClick={handleOpenCreateCompany}>
                <AppIcon name="add_business" />
                Adicionar empresa
              </button>
            </div>
            {/* Barra de busca */}
            <div className="companies-search">
              <div className="search-input-wrapper">
                <AppIcon name="search" />
                <input
                  type="text"
                  placeholder="Buscar empresa por nome, e-mail ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {/* Tabela de empresas */}
            <div className="companies-table-container">
              <table className="companies-table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>E-mail</th>
                    <th>Telefone</th>
                    <th>Status</th>
                    <th>Data de Cadastro</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {companies
                    .filter(company => 
                      company.nome_fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      company.email_contato?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      company.telefone_principal?.includes(searchTerm)
                    )
                    .map(company => (
                      <tr key={company.company_id}>
                        <td>
                          <div className="company-name-cell">
                            <AppIcon name="business" />
                            <strong>{company.nome_fantasia}</strong>
                          </div>
                        </td>
                        <td>{company.email_contato}</td>
                        <td>{company.telefone_principal || '—'}</td>
                        <td>
                          <span className={`status-badge ${company.status_empresa?.toLowerCase()}`}>
                            {company.status_empresa || 'ATIVO'}
                          </span>
                        </td>
                        <td>
                          {company.created_at 
                            ? new Date(company.created_at).toLocaleDateString('pt-BR')
                            : '—'}
                        </td>
                        <td>
                          <button
                            className="action-button view"
                            onClick={() => handleOpenCompanyModal(company.company_id)}
                            title="Ver detalhes"
                          >
                            <AppIcon name="visibility" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {companies.length === 0 && (
                <div className="empty-state">
                  <AppIcon name="business" />
                  <p>Nenhuma empresa encontrada</p>
                </div>
              )}
            </div>

            {/* Modal de Criação de Empresa */}
            {showCreateCompanyModal && (
              <div className="modal-overlay" onClick={handleCloseCreateCompany}>
                <div className="modal-content create-company-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <div className="modal-header-content">
                      <AppIcon name="add_business" className="modal-icon" />
                      <h2>Adicionar nova empresa</h2>
                    </div>
                    <button className="modal-close-button" onClick={handleCloseCreateCompany} disabled={creatingCompany}>
                      <AppIcon name="close" />
                    </button>
                  </div>

                  <form className="create-company-form" onSubmit={handleCreateCompanySubmit}>
                    <div className="form-group">
                      <label>
                        <AppIcon name="store" />
                        Nome da Empresa *
                      </label>
                      <input
                        type="text"
                        name="nome_empresa"
                        value={createCompanyForm.nome_empresa}
                        onChange={handleCreateCompanyChange}
                        placeholder="Digite o nome"
                        required
                        disabled={creatingCompany}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <AppIcon name="workspace_premium" />
                        Plano *
                      </label>
                      <select
                        name="plan_id"
                        value={createCompanyForm.plan_id}
                        onChange={handleCreateCompanyChange}
                        required
                        disabled={creatingCompany}
                      >
                        <option value="">Selecione um plano</option>
                        {availablePlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - {plan.monthly_credits?.toLocaleString('pt-BR')} créditos/mês
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <AppIcon name="category" />
                          Segmento *
                        </label>
                        <select
                          name="segmento"
                          value={createCompanyForm.segmento}
                          onChange={handleCreateCompanyChange}
                          required
                          disabled={creatingCompany}
                        >
                          {SEGMENTOS.map((segment) => (
                            <option key={segment.value} value={segment.value}>
                              {segment.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {createCompanyForm.segmento && SUBSEGMENTOS[createCompanyForm.segmento]?.length > 0 && (
                        <div className="form-group">
                          <label>
                            <AppIcon name="label" />
                            Tipo de Negócio
                          </label>
                          <select
                            name="subsegmento"
                            value={createCompanyForm.subsegmento}
                            onChange={handleCreateCompanyChange}
                            disabled={creatingCompany}
                          >
                            {SUBSEGMENTOS[createCompanyForm.segmento].map((sub) => (
                              <option key={sub.value} value={sub.value}>
                                {sub.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <AppIcon name="mail" />
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={createCompanyForm.email}
                        onChange={handleCreateCompanyChange}
                        placeholder="empresa@email.com"
                        required
                        disabled={creatingCompany}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <AppIcon name="phone" />
                        WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={createCompanyForm.whatsapp}
                        onChange={handleCreateCompanyChange}
                        placeholder="(00) 00000-0000"
                        required
                        disabled={creatingCompany}
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <AppIcon name="lock" />
                          Senha *
                        </label>
                        <input
                          type="password"
                          name="senha"
                          value={createCompanyForm.senha}
                          onChange={handleCreateCompanyChange}
                          placeholder="Mínimo 6 caracteres"
                          required
                          disabled={creatingCompany}
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          <AppIcon name="lock_reset" />
                          Confirmar Senha *
                        </label>
                        <input
                          type="password"
                          name="confirmar_senha"
                          value={createCompanyForm.confirmar_senha}
                          onChange={handleCreateCompanyChange}
                          placeholder="Repita a senha"
                          required
                          disabled={creatingCompany}
                        />
                      </div>
                    </div>

                    {createCompanyError && (
                      <div className="error-message">
                        <AppIcon name="error" />
                        {createCompanyError}
                      </div>
                    )}

                    <div className="modal-footer">
                      <button type="button" className="cancel-button" onClick={handleCloseCreateCompany} disabled={creatingCompany}>
                        Cancelar
                      </button>
                      <button type="submit" className="save-button" disabled={creatingCompany}>
                        {creatingCompany ? (
                          <>
                            <AppIcon name="progress_activity" className="spinning" />
                            Criando...
                          </>
                        ) : (
                          <>
                            <AppIcon name="check" />
                            Criar empresa
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal de Detalhes da Empresa */}
            {showCompanyModal && (
              <div className="modal-overlay" onClick={handleCloseCompanyModal}>
                <div className="modal-content company-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>
                      <AppIcon name="business" />
                      Detalhes da Empresa
                    </h2>
                    <button className="modal-close-button" onClick={handleCloseCompanyModal}>
                      <AppIcon name="close" />
                    </button>
                  </div>

                  {loadingDetails ? (
                    <div className="modal-loading">
                      <LoadingSpinner />
                    </div>
                  ) : companyDetails && (
                    <div className="modal-body">
                      {/* Informações Básicas */}
                      <div className="details-section">
                <h4>
                  <AppIcon name="business" />
                  Informações Básicas
                </h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Nome Fantasia</label>
                    <span>{companyDetails.nome_fantasia}</span>
                  </div>
                  <div className="detail-item">
                    <label>Razão Social</label>
                    <span>{companyDetails.razao_social || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <label>CNPJ</label>
                    <span>{companyDetails.cnpj || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <label>E-mail</label>
                    <span>{companyDetails.email_contato}</span>
                  </div>
                  <div className="detail-item">
                    <label>Telefone</label>
                    <span>{companyDetails.telefone_principal}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${companyDetails.status_empresa?.toLowerCase()}`}>
                      {companyDetails.status_empresa}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Segmento</label>
                    <span>{companyDetails.segmento || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Subsegmento</label>
                    <span>{companyDetails.subsegmento || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Plano e Assinatura */}
              <div className="details-section">
                <div className="section-header-with-action">
                  <h4>
                    <AppIcon name="workspace_premium" />
                    Plano e Assinatura
                  </h4>
                  <button 
                    className="edit-button"
                    onClick={handleOpenEditPlan}
                    title="Editar plano"
                  >
                    <AppIcon name="edit" />
                    Editar Plano
                  </button>
                </div>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Plano Atual</label>
                    <span className="plan-badge">
                      {companyDetails.subscription?.plans?.name || 'Sem plano'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status da Assinatura</label>
                    <span className={`status-badge ${companyDetails.subscription?.status}`}>
                      {companyDetails.subscription?.status || 'inactive'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Data de Início</label>
                    <span>
                      {companyDetails.subscription?.started_at 
                        ? new Date(companyDetails.subscription.started_at).toLocaleDateString('pt-BR')
                        : '—'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Data de Renovação</label>
                    <span>
                      {companyDetails.subscription?.expires_at 
                        ? new Date(companyDetails.subscription.expires_at).toLocaleDateString('pt-BR')
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Créditos */}
              <div className="details-section">
                <h4>
                  <AppIcon name="toll" />
                  Créditos
                </h4>
                <div className="credits-info">
                  <div className="credit-card">
                    <label>Disponíveis</label>
                    <span className="credit-value available">
                      {companyDetails.credits?.credits_available?.toLocaleString('pt-BR') || 0}
                    </span>
                  </div>
                  <div className="credit-card">
                    <label>Usados</label>
                    <span className="credit-value used">
                      {companyDetails.credits?.credits_used?.toLocaleString('pt-BR') || 0}
                    </span>
                  </div>
                  <div className="credit-card">
                    <label>Total</label>
                    <span className="credit-value total">
                      {companyDetails.credits?.credits_total?.toLocaleString('pt-BR') || 0}
                    </span>
                  </div>
                  <div className="credit-card">
                    <label>Bônus</label>
                    <span className="credit-value bonus">
                      {companyDetails.credits?.credits_bonus?.toLocaleString('pt-BR') || 0}
                    </span>
                  </div>
                </div>
                <div className="credits-period">
                  <span>
                    Período: {companyDetails.credits?.period_start 
                      ? new Date(companyDetails.credits.period_start).toLocaleDateString('pt-BR')
                      : '—'} até {companyDetails.credits?.period_end 
                      ? new Date(companyDetails.credits.period_end).toLocaleDateString('pt-BR')
                      : '—'}
                  </span>
                  <div className="credits-add">
                    <input
                      type="number"
                      min="1"
                      placeholder="Qtd. créditos"
                      value={creditAmount}
                      onChange={(event) => setCreditAmount(event.target.value)}
                      disabled={addingCredits}
                    />
                    <button
                      className="edit-button"
                      onClick={handleAddCredits}
                      disabled={addingCredits}
                    >
                      {addingCredits ? 'Adicionando...' : 'Adicionar créditos'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Estatísticas de Uso */}
              <div className="details-section">
                <h4>
                  <AppIcon name="analytics" />
                  Estatísticas de Uso
                </h4>
                <div className="stats-grid-small">
                  <div 
                    className={`stat-card-small clickable ${expandedSection === 'users' ? 'expanded' : ''}`}
                    onClick={() => handleToggleSection('users')}
                  >
                    <AppIcon name="group" />
                    <div>
                      <label>Usuários</label>
                      <strong>{companyDetails.stats?.users || 0}</strong>
                    </div>
                    <AppIcon name={expandedSection === 'users' ? 'expand_less' : 'expand_more'} className="expand-icon" />
                  </div>
                  <div 
                    className={`stat-card-small clickable ${expandedSection === 'agents' ? 'expanded' : ''}`}
                    onClick={() => handleToggleSection('agents')}
                  >
                    <AppIcon name="smart_toy" />
                    <div>
                      <label>Agentes</label>
                      <strong>{companyDetails.stats?.agents || 0}</strong>
                    </div>
                    <AppIcon name={expandedSection === 'agents' ? 'expand_less' : 'expand_more'} className="expand-icon" />
                  </div>
                  <div 
                    className={`stat-card-small clickable ${expandedSection === 'connections' ? 'expanded' : ''}`}
                    onClick={() => handleToggleSection('connections')}
                  >
                    <AppIcon name="link" />
                    <div>
                      <label>Conexões</label>
                      <strong>{companyDetails.stats?.connections || 0}</strong>
                    </div>
                    <AppIcon name={expandedSection === 'connections' ? 'expand_less' : 'expand_more'} className="expand-icon" />
                  </div>
                  <div 
                    className={`stat-card-small clickable ${expandedSection === 'contacts' ? 'expanded' : ''}`}
                    onClick={() => handleToggleSection('contacts')}
                  >
                    <AppIcon name="contacts" />
                    <div>
                      <label>Contatos</label>
                      <strong>{companyDetails.stats?.contacts || 0}</strong>
                    </div>
                    <AppIcon name={expandedSection === 'contacts' ? 'expand_less' : 'expand_more'} className="expand-icon" />
                  </div>
                  <div 
                    className={`stat-card-small clickable ${expandedSection === 'conversations' ? 'expanded' : ''}`}
                    onClick={() => handleToggleSection('conversations')}
                  >
                    <AppIcon name="chat" />
                    <div>
                      <label>Conversas</label>
                      <strong>{companyDetails.stats?.conversations || 0}</strong>
                    </div>
                    <AppIcon name={expandedSection === 'conversations' ? 'expand_less' : 'expand_more'} className="expand-icon" />
                  </div>
                </div>

                {/* Lista Expandida */}
                {expandedSection && (
                  <div className="expanded-list">
                    {loadingSection ? (
                      <div className="loading-section">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        {/* Usuários */}
                        {expandedSection === 'users' && (
                          <div className="section-list">
                            <h5>Lista de Usuários ({sectionData.users.length})</h5>
                            {sectionData.users.map(user => (
                              <div key={user.id} className="list-item">
                                <div className="list-item-info">
                                  <strong>{user.nome || user.email}</strong>
                                  <span>{user.email}</span>
                                  <span className="item-meta">
                                    Nível: {user.nivel_acesso} | Plano: {user.plano_conta || '—'}
                                    {user.is_superuser && <span className="badge-super">SUPER</span>}
                                  </span>
                                </div>
                                <div className="list-item-actions">
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteUser(user.id)}
                                    title="Excluir usuário"
                                  >
                                    <AppIcon name="delete" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {sectionData.users.length === 0 && (
                              <p className="empty-message">Nenhum usuário encontrado</p>
                            )}
                          </div>
                        )}

                        {/* Agentes */}
                        {expandedSection === 'agents' && (
                          <div className="section-list">
                            <h5>Lista de Agentes ({sectionData.agents.length})</h5>
                            {sectionData.agents.map(agent => (
                              <div key={agent.id} className="list-item">
                                <div className="list-item-info">
                                  <strong>{agent.nome}</strong>
                                  <span>{agent.descricao || '—'}</span>
                                  <span className="item-meta">
                                    Canal: {agent.canal} | Função: {agent.funcao_principal || '—'}
                                  </span>
                                </div>
                                <div className="list-item-actions">
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteAgent(agent.id)}
                                    title="Excluir agente"
                                  >
                                    <AppIcon name="delete" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {sectionData.agents.length === 0 && (
                              <p className="empty-message">Nenhum agente encontrado</p>
                            )}
                          </div>
                        )}

                        {/* Conexões */}
                        {expandedSection === 'connections' && (
                          <div className="section-list">
                            <h5>Lista de Conexões ({sectionData.connections.length})</h5>
                            {sectionData.connections.map(conn => (
                              <div key={conn.id} className="list-item">
                                <div className="list-item-info">
                                  <strong>{conn.name}</strong>
                                  <span>{conn.number || '—'}</span>
                                  <span className="item-meta">
                                    Tipo: {conn.type} | Status: {conn.status}
                                  </span>
                                </div>
                                <div className="list-item-actions">
                                  <button 
                                    className="action-btn delete"
                                    onClick={() => handleDeleteConnection(conn.id)}
                                    title="Excluir conexão"
                                  >
                                    <AppIcon name="delete" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {sectionData.connections.length === 0 && (
                              <p className="empty-message">Nenhuma conexão encontrada</p>
                            )}
                          </div>
                        )}

                        {/* Contatos */}
                        {expandedSection === 'contacts' && (
                          <div className="section-list">
                            <h5>Lista de Contatos (primeiros 100)</h5>
                            {sectionData.contacts.map(contact => (
                              <div key={contact.id} className="list-item">
                                <div className="list-item-info">
                                  <strong>{contact.nome || 'Sem nome'}</strong>
                                  <span>{contact.telefone || '—'}</span>
                                  <span className="item-meta">
                                    {contact.email || '—'}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {sectionData.contacts.length === 0 && (
                              <p className="empty-message">Nenhum contato encontrado</p>
                            )}
                          </div>
                        )}

                        {/* Conversas */}
                        {expandedSection === 'conversations' && (
                          <div className="section-list">
                            <h5>Lista de Conversas (últimas 100)</h5>
                            {sectionData.conversations.map(conv => (
                              <div key={conv.id} className="list-item">
                                <div className="list-item-info">
                                  <strong>Conversa #{conv.id.substring(0, 8)}</strong>
                                  <span className="item-meta">
                                    Status: {conv.status} | Atualizada: {new Date(conv.updated_at).toLocaleString('pt-BR')}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {sectionData.conversations.length === 0 && (
                              <p className="empty-message">Nenhuma conversa encontrada</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Zona de Perigo */}
              <div className="modal-footer danger-zone">
                <div className="danger-zone-content">
                  <div className="danger-zone-info">
                    <h4>
                      <AppIcon name="warning" />
                      Zona de Perigo
                    </h4>
                    <p>Ações irreversíveis que afetam permanentemente esta empresa.</p>
                  </div>
                  <button 
                    className="delete-company-button"
                    onClick={handleDeleteCompany}
                  >
                    <AppIcon name="delete_forever" />
                    Deletar Empresa
                  </button>
                </div>
              </div>

                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal de Edição de Plano */}
            {showEditPlanModal && (
              <div className="modal-overlay" onClick={() => setShowEditPlanModal(false)}>
                <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>
                      <AppIcon name="workspace_premium" />
                      Editar Plano
                    </h3>
                    <button 
                      className="close-button"
                      onClick={() => setShowEditPlanModal(false)}
                    >
                      <AppIcon name="close" />
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="form-group">
                      <label>Plano</label>
                      <select
                        value={editPlanData.plan_id}
                        onChange={(e) => setEditPlanData({...editPlanData, plan_id: e.target.value})}
                      >
                        <option value="">Selecione um plano</option>
                        {availablePlans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} - R$ {plan.price}/mês
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={editPlanData.status}
                        onChange={(e) => setEditPlanData({...editPlanData, status: e.target.value})}
                      >
                        <option value="active">Ativo</option>
                        <option value="inactive">Inativo</option>
                        <option value="cancelled">Cancelado</option>
                        <option value="suspended">Suspenso</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Data de Renovação</label>
                      <input
                        type="date"
                        value={editPlanData.renewal_date}
                        onChange={(e) => setEditPlanData({...editPlanData, renewal_date: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button 
                      className="cancel-button"
                      onClick={() => setShowEditPlanModal(false)}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="save-button"
                      onClick={handleSavePlan}
                    >
                      <AppIcon name="save" />
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
